import mergerino from 'mergerino';
import { hasAction } from './auth';
import t from '../lib/translate';

export const allOptionsMap = {
  calculationEngine: {
    at: 'Advantech-Refrion',
    luveCore: 'Lu-Ve Professional',
  },
  calculationEngineStatus: {
    luveCore: 'configurator.enable_luve_core',
  },
  productType: {
    acc: 'configurator.acc',
    lc: 'configurator.lc',
    lhp: 'configurator.lhp',
    ltht: 'configurator.ltht',
  },
  range: {
    all: 'configurator.range_all',
    ecooler: 'configurator.range_ecooler',
    hv_airflow: 'configurator.range_hv_airflow',
    other: 'configurator.range_other',
    radial: 'configurator.range_radial',
    table: 'configurator.range_table',
    vshape: 'configurator.range_vshape',
    vshape_series: 'configurator.range_vshape_series',
    wall: 'configurator.range_wall',
  },
  family: {
    // ACC
    hi_tech_giant_s_tower: 'configurator.family_hi_tech_giant_s_tower',
    hi_duty_alfa_v_combo: 'configurator.family_hi_duty_alfa_v_combo',
    hi_duty_alfa_v_wall: 'configurator.family_hi_duty_alfa_v_wall',
    hi_tech_giant_f_hv_airflow: 'configurator.family_hi_tech_giant_f_hv_airflow',
    hi_tech_giant_c: 'configurator.family_hi_tech_giant_c',
    hi_tech_giant_c_hv_microchannel: 'configurator.family_hi_tech_giant_c_hv_microchannel',
    hi_tech_minichannel: 'configurator.family_hi_tech_minichannel',
    hi_tech_giant_f_radial: 'configurator.family_hi_tech_giant_f_radial',

    // LC / LHP
    hi_duty_alfa_v_combo_ul_csa: 'configurator.family_hi_duty_alfa_v_combo_ul_csa',
    hi_duty_alfa_v_mega_jumbo: 'configurator.family_hi_duty_alfa_v_mega_jumbo',
    hi_duty_alfa_v_mega_jumbo_ul_csa: 'configurator.family_hi_duty_alfa_v_mega_jumbo_ul_csa',
    hi_duty_alfa_v_mega_wall: 'configurator.family_hi_duty_alfa_v_mega_wall',
    hi_duty_alfa_v_mega_wall_ul_csa: 'configurator.family_hi_duty_alfa_v_mega_wall_ul_csa',
    hi_tech_giant_s_tower_ul_csa: 'configurator.family_hi_tech_giant_s_tower_ul_csa',
    hi_tech_giant_f_hv_airflow_ul_csa: 'configurator.family_hi_tech_giant_f_hv_airflow_ul_csa',
    hi_duty_alfa_ecooler: 'configurator.family_hi_duty_alfa_ecooler',
    hi_duty_alfa_ecooler_container: 'configurator.family_hi_duty_alfa_ecooler_container',
  },
  condition: {
    drySystem: 'configurator.condition_dry_system',
    sprayAdiabaticSystem: 'configurator.condition_spray_adiabatic_system',
    hybridSpraySystem: 'configurator.condition_hybrid_spray_system',
    hybridSpraySystemHybrid: 'configurator.condition_hybrid_spray_system_hybrid',
    hybridSpraySystemEvolution: 'configurator.condition_hybrid_spray_system_evolution',
    industrialAdiabaticSystem: 'configurator.condition_industrial_adiabatic_system',
    industrialAdiabaticSystemWithRecirculation: 'configurator.condition_industrial_adiabatic_system_with_recirculation',
  },
  priority: {
    cooling: 'configurator.priority_cooling',
    heating: 'configurator.priority_heating',
    onlyCooling: 'configurator.priority_only_cooling',
    onlyHeating: 'configurator.priority_only_heating',
  },
  heatExchangers: {
    microchannel: 'configurator.heat_exchangers_microchannel',
    ovalTube: 'configurator.heat_exchangers_oval_tube',
    roundTube: 'configurator.heat_exchangers_round_tube',
  },
  padType: {
    PVC: 'configurator.pad_type_pvc',
    CART_150: 'configurator.pad_type_cart_150',
  },
};

export const families = {
  acc: {
    vshape: ['hi_tech_giant_s_tower', 'hi_duty_alfa_v_combo', 'hi_duty_alfa_v_wall'],
    table: ['hi_tech_giant_f_hv_airflow', 'hi_tech_giant_c', 'hi_tech_giant_c_hv_microchannel', 'hi_tech_minichannel'],
    other: ['hi_tech_giant_f_radial'],
  },
  lc: {
    vshape: [
      'hi_tech_giant_s_tower',
      'hi_tech_giant_s_tower_ul_csa',
      'hi_duty_alfa_v_combo',
      'hi_duty_alfa_v_combo_ul_csa',
      'hi_duty_alfa_v_mega_jumbo',
      'hi_duty_alfa_v_mega_jumbo_ul_csa',
      'hi_duty_alfa_v_mega_wall',
      'hi_duty_alfa_v_mega_wall_ul_csa',
      'hi_duty_alfa_v_wall',
    ],
    table: ['hi_tech_giant_f_hv_airflow', 'hi_tech_giant_f_hv_airflow_ul_csa'],
    ecooler: ['hi_duty_alfa_ecooler', 'hi_duty_alfa_ecooler_container'],
    other: ['hi_tech_giant_f_radial'],
  },
  lhp: {
    vshape: [
      'hi_tech_giant_s_tower',
      'hi_duty_alfa_v_combo',
      'hi_duty_alfa_v_mega_jumbo',
      'hi_duty_alfa_v_wall',
      'hi_duty_alfa_v_mega_wall',
    ],
    table: ['hi_tech_giant_f_hv_airflow'],
  },
};

export const optionsMap = mergerino(allOptionsMap, {
  productType: {
    ltht: undefined,
  },
  condition: {
    hybridSpraySystemHybrid: undefined,
  },
});

export const customOptionsMap = mergerino(allOptionsMap);

// Only these conditions have an adiabatic pad, so they are the only ones
// requiring a pad type
export const conditionsWithPadType = ['industrialAdiabaticSystem', 'industrialAdiabaticSystemWithRecirculation'];

export const getOptions = (prop, { isCustom = false } = {}) => {
  const optsMap = isCustom ? customOptionsMap : optionsMap;
  return Object.keys(optsMap[prop]).map((key) => ({
    value: key,
    label: t(optsMap[prop][key]),
  }));
};

export const customFamilies = {
  ...families,
  ltht: {
    vshape: ['tower', 'combo', 'superjumbo', 'wall'],
    table: ['hv_airflow'],
  },
};

// CAUTION! do not change the format of the option without adapting the parsing
// in 'getAllowedPowerSourceOptions' accordingly
export const powerSourceOptions = {
  '1ph50Hz230V': '1ph / 50Hz / 230V',
  '3ph50Hz400V': '3ph / 50Hz / 400V',
  '3ph60Hz230V': '3ph / 60Hz / 230V',
  '3ph60Hz460V': '3ph / 60Hz / 460V',
  '3ph60Hz575V': '3ph / 60Hz / 575V',
};

export const energyEfficiencyClassOptions = {
  'A+': 'A+',
  A: 'A',
  B: 'B',
  c: 'C',
  D: 'D',
  E: 'E',
};

export const getAllowedFamilies = () =>
  Object.keys(families).reduce((acc, curr) => {
    if (hasAction(`configurator.product_type_${curr}`)) {
      acc[curr] = Object.keys(families[curr]).reduce((accumulator, key) => {
        const allowedFamilies = families[curr][key].filter((family) =>
          hasAction(`configurator.family_${curr}_${family}`),
        );
        if (allowedFamilies.length) accumulator[key] = allowedFamilies;
        return accumulator;
      }, {});
    }
    return acc;
  }, {});

export const getAvailableFamiliesByType = (productType) => {
  if (!hasAction(`configurator.product_type_${productType}`)) return {};
  const allowedFamilies = getAllowedFamilies();
  return Object.keys(allowedFamilies[productType]).reduce(
    (accumulator, key) => ({
      ...accumulator,
      ...allowedFamilies[productType][key].reduce((acc, family) => {
        acc[family] = true;
        return acc;
      }, {}),
    }),
    {},
  );
};

export const getOption = (entry) => ({
  value: entry.id,
  label: entry.name,
});

export const getFluidOption = (entry) => ({
  value: entry.id,
  label: entry.safetyClassName ? `${entry.name} (${entry.safetyClassName})` : entry.name,
});

export const getAllowedPowerSourceOptions = () =>
  Object.keys(powerSourceOptions).reduce((acc, curr) => {
    if (hasAction(`configurator.power_source_freq_${powerSourceOptions[curr].split(' / ')[1].toLowerCase()}`)) {
      acc[curr] = powerSourceOptions[curr];
    }
    return acc;
  }, {});
